import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children?: ReactNode;
  footer?: ReactNode;
  className?: string;
}

/**
 * Edge panel for filters and detail views, sharing the native `<dialog>` behaviour of Modal.
 *
 * It anchors to the inline end (`ms-auto` + `me-0`), so it slides in from the right in English and
 * from the left in Arabic without a direction-specific rule.
 */
export function Drawer({ isOpen, onClose, title, children, footer, className }: DrawerProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen && !dialog.open) dialog.showModal();
    if (!isOpen && dialog.open) dialog.close();
  }, [isOpen]);

  return (
    <dialog
      ref={dialogRef}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClose={onClose}
      onClick={(event) => {
        if (event.target === dialogRef.current) onClose();
      }}
      aria-labelledby="drawer-title"
      className={cn(
        'border-line ms-auto me-0 h-dvh max-h-dvh w-full max-w-md border-s bg-white p-0 shadow-xl',
        'backdrop:bg-black/40',
        className,
      )}
    >
      <div className="flex h-full flex-col">
        <div className="border-line flex items-center justify-between gap-4 border-b px-5 py-4">
          <h2 id="drawer-title" className="text-ink truncate text-base font-semibold">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="text-muted hover:text-ink -me-1 shrink-0"
          >
            <X className="size-5" aria-hidden />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {footer ? (
          <div className="border-line bg-canvas/60 flex flex-wrap justify-end gap-2 border-t px-5 py-3">
            {footer}
          </div>
        ) : null}
      </div>
    </dialog>
  );
}
